import React, { useEffect } from 'react';

const sections = [
  {
    number: '1',
    title: 'Agreement to These Terms',
    content: `These Terms and Conditions ("Terms") govern your access to and use of the SpotFinder platform operated by SpotFinder Kenya Limited ("SpotFinder", "we", "us", or "our"), including mobile applications (Customer, Manager, SpotGenie), web portals (admin and operations), API services at https://api.spotfinder.co.ke, and related payment or QR pages (the "Platform").

By creating an account, ticking an acceptance box at login, continuing to use the Platform after we publish updated Terms, or making a payment, you agree to these Terms and our Privacy Policy. If you do not agree, do not use the Platform.

These Terms are written to be fair and understandable, in line with Kenyan consumer protection standards and regional best practice for marketplace platforms.`,
  },
  {
    number: '2',
    title: 'Definitions',
    content: null,
    definitions: [
      { term: 'Platform', def: "SpotFinder's software, websites, APIs, and related services." },
      { term: 'Customer', def: 'A user who searches for, books, or pays for parking.' },
      { term: 'Manager', def: 'An independent parking facility operator who lists parking on the Platform.' },
      { term: 'SpotGenie', def: 'An authorised on-site agent who assists with walk-in parking and payments.' },
      { term: 'Parking Operator', def: 'The Manager (and their staff) responsible for a physical parking facility.' },
      { term: 'Booking', def: 'A reserved parking session created through the Platform for a defined time or product.' },
      { term: 'Walk-in', def: 'A parking session started on-site without a prior booking, including QR payment flows.' },
      { term: 'Wallet', def: 'A stored-value balance within the Platform used for eligible payments and refunds.' },
      { term: 'Fees', def: 'Amounts displayed before you confirm payment, including parking charges, taxes, coupons, and platform charges where shown.' },
    ],
  },
  {
    number: '3',
    title: 'The SpotFinder Service',
    content: 'SpotFinder provides a technology platform that connects Customers with Parking Operators and SpotGenies. SpotFinder does not own or operate parking facilities unless explicitly stated for a specific site.',
    bullets: [
      'We facilitate discovery, booking, slot allocation, payments, receipts, and notifications.',
      'We provide tools for Managers to configure pricing, availability, KYC, and settlements.',
      'We provide tools for SpotGenies to register walk-ins, collect payments, and manage on-site flows.',
      'Where enabled, we integrate with ALPR/gate systems to validate entry and exit — subject to site configuration.',
    ],
    footer: 'Except where expressly stated, the parking service itself (space quality, lighting, security personnel, signage, towing, and compliance with local bylaws) is provided by the Parking Operator, not by SpotFinder.',
  },
  {
    number: '4',
    title: 'Eligibility and Accounts',
    bullets: [
      'You must be at least 18 years old and capable of entering a binding contract under Kenyan law.',
      'You must provide accurate registration information and keep it updated.',
      'You are responsible for safeguarding your login credentials. Authentication is handled via Firebase; do not share OTP codes or passwords.',
      'One person may not maintain multiple accounts to evade restrictions, fraud checks, or bans.',
      'We may refuse registration or deactivate accounts that violate these Terms or applicable law.',
    ],
  },
  {
    number: '5',
    title: 'Bookings, Walk-ins, and Parking',
    subsections: [
      {
        title: '5.1 Bookings',
        bullets: [
          'A Booking is confirmed when shown as confirmed in the app and, where applicable, payment succeeds.',
          'You must arrive within the booked window and display the registered plate number unless the site allows alternate verification.',
          'Parking Operators may refuse entry if you breach site rules, present a different vehicle, or pose a safety risk — without entitlement to a refund except as stated in our cancellation policy.',
        ],
      },
      {
        title: '5.2 Walk-ins and QR payments',
        bullets: [
          'Walk-in fees are displayed before payment is initiated (M-Pesa STK push, card, wallet, or other enabled method).',
          "Payment links and QR codes point to SpotFinder's secure cloud payment pages; you should verify the domain before paying.",
          'Exit or gate release may require confirmed payment and valid session status recorded on the Platform.',
        ],
      },
      {
        title: '5.3 Overstay',
        content: "If you remain beyond your booked or paid period, additional charges may apply automatically according to the Parking Operator's configured pricing (fixed penalty or hourly extension). You will be shown the amount due before confirming any overstay payment where the app supports it.",
      },
    ],
  },
  {
    number: '6',
    title: 'Pricing and Payment',
    bullets: [
      'Fees are displayed in Kenyan Shillings (KES) unless another currency is clearly shown for cross-border wallets.',
      'The price shown at checkout is the price we intend to charge before you authorise payment.',
      'Payments may be processed via M-Pesa, Paystack (cards/mobile money), or your Platform Wallet, depending on enabled methods.',
      'Wallet top-ups and parking payments are subject to successful confirmation from the payment provider.',
      'Taxes and coupons are applied as shown on the checkout summary.',
      'SpotFinder may charge platform or service fees to Managers or Customers where disclosed in the checkout flow or Manager agreement.',
    ],
  },
  {
    number: '7',
    title: 'Cancellations and Refunds',
    content: 'Cancellation and refund rules are also published as a separate Cancellation and Refund Policy which you accept through the Platform. Summary of key rules:',
    bullets: [
      'Customer booking cancellation within the applicable cancellation window: refund credited to your Platform Wallet.',
      'Late cancellation (after the cancellation window): generally no refund; revenue may be shared between the Parking Operator and SpotFinder.',
      'Walk-in payments: refunds are generally not available once a session is completed except where required by law or approved by support for proven platform error.',
      'Approved refunds for eligible payments are processed to the Wallet unless we expressly agree otherwise in writing.',
    ],
    footer: 'Nothing in these Terms limits your statutory rights under Kenyan consumer protection law where those rights cannot be excluded.',
  },
  {
    number: '8',
    title: 'Parking Operators and SpotGenies',
    bullets: [
      'Managers and SpotGenies are independent third parties, not employees or agents of SpotFinder except where SpotFinder explicitly acts as a disclosed principal.',
      'Managers set prices, availability, and facility rules within Platform parameters.',
      'Managers are responsible for permits, insurance, safety, and lawful operation of their facilities.',
      "SpotGenies act under the Manager's authority at the site; SpotFinder provides the software they use.",
      'Disputes about vehicle damage, theft, towing, or on-site conduct should first be raised with the Parking Operator; SpotFinder will reasonably assist with transaction records.',
    ],
  },
  {
    number: '9',
    title: 'Acceptable Use',
    content: 'You must not:',
    bullets: [
      'Use the Platform for unlawful purposes, fraud, or payment chargeback abuse.',
      "Interfere with servers, API rate limits, gate systems, or other users' access.",
      'Upload malware, scrape data without permission, or reverse engineer the Platform except as permitted by law.',
      'Misrepresent your identity, plate number, or payment credentials.',
      'Harass Managers, SpotGenies, or support staff.',
    ],
  },
  {
    number: '10',
    title: 'Vehicle, Property, and Personal Safety',
    content: 'Parking involves real-world risks. To the fullest extent permitted by Kenyan law:',
    bullets: [
      'You park at your own risk. SpotFinder does not guarantee the security of vehicles or belongings.',
      'SpotFinder is not responsible for damage, theft, fire, flooding, or towing conducted by third parties unless directly caused by our proven negligence in operating the Platform.',
      'ALPR and automated gates are aids only; you remain responsible for safe driving and verifying barriers before proceeding.',
    ],
  },
  {
    number: '11',
    title: 'Platform Availability and Changes',
    bullets: [
      'We strive for reliable service but do not guarantee uninterrupted access. Maintenance, outages, or third-party failures may occur.',
      'We may modify, suspend, or discontinue features with reasonable notice where practicable.',
      'We may update these Terms. Material changes will be notified in-app or by email; continued use after the effective date constitutes acceptance unless you delete your account.',
    ],
  },
  {
    number: '12',
    title: 'Intellectual Property',
    content: "SpotFinder and its licensors own all rights in the Platform, trademarks, software, and content we provide. You receive a limited, non-exclusive, revocable licence to use the Platform for personal or authorised business use in accordance with these Terms. You must not copy or resell the Platform without written consent.",
  },
  {
    number: '13',
    title: 'Suspension and Termination',
    bullets: [
      'You may stop using the Platform and request account deletion at any time, subject to settlement of outstanding amounts.',
      'We may suspend or terminate access immediately for breach, fraud, legal requirement, or risk to users or SpotFinder.',
      'Where termination is not for your breach, we will provide notice where reasonable and process Wallet balances in accordance with applicable rules.',
    ],
  },
  {
    number: '14',
    title: 'Disclaimers',
    content: 'The Platform is provided "as is" and "as available". Except as expressly stated in these Terms or required by law, SpotFinder disclaims all warranties, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Platform will be error-free or that parking facilities will meet your expectations.',
  },
  {
    number: '15',
    title: 'Limitation of Liability',
    content: "This section limits SpotFinder's liability in a manner intended to be valid under Kenyan law:",
    bullets: [
      'SpotFinder is not liable for acts or omissions of Parking Operators, SpotGenies, payment networks, cloud providers, or other third parties outside our reasonable control.',
      'SpotFinder is not liable for indirect, incidental, special, consequential, or punitive damages, including lost profits, data loss, or business interruption.',
      "SpotFinder's total aggregate liability shall not exceed the greater of: (a) total Fees you paid in the twelve months before the event; or (b) KES 10,000.",
      'Nothing in these Terms excludes liability that cannot be excluded under Kenyan law, including liability for death or personal injury caused by negligence, fraud, or wilful misconduct.',
    ],
  },
  {
    number: '16',
    title: 'Indemnity',
    content: "You agree to indemnify and hold harmless SpotFinder, its directors, officers, employees, and contractors from claims, losses, and expenses (including reasonable legal fees) arising from: (a) your breach of these Terms; (b) your misuse of the Platform; (c) your violation of law or third-party rights; or (d) disputes between you and a Parking Operator to the extent caused by your conduct.",
  },
  {
    number: '17',
    title: 'Dispute Resolution and Governing Law',
    bullets: [
      'These Terms are governed by the laws of the Republic of Kenya.',
      'Parties submit to the exclusive jurisdiction of the courts of Kenya sitting in Nairobi.',
      'We encourage you to contact support@spotfinder.co.ke first so we can attempt good-faith resolution within thirty (30) days.',
      'Nothing prevents you from referring a complaint to the ODPC (data protection) or the Competition Authority of Kenya (consumer protection) where applicable.',
    ],
  },
  {
    number: '18',
    title: 'Force Majeure',
    content: 'SpotFinder is not liable for delay or failure caused by events beyond reasonable control, including natural disasters, war, civil unrest, power or internet failures, government actions, strikes, or failures of telecommunications or payment systems.',
  },
  {
    number: '19',
    title: 'General',
    bullets: [
      'If any provision is held invalid, the remainder stays in effect.',
      'Our failure to enforce a provision is not a waiver.',
      'You may not assign your rights without our consent; we may assign to an affiliate or successor.',
      'These Terms, the Privacy Policy, and accepted policies form the entire agreement regarding the Platform.',
    ],
  },
];

export default function Terms() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Terms & Conditions — Spot Finder';
  }, []);

  return (
    <main id="main-content">
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #00A878 0%, #007a58 100%)',
          padding: '80px 24px 60px',
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span
            style={{
              display: 'inline-block',
              background: 'rgba(255,255,255,0.18)',
              border: '1px solid rgba(255,255,255,0.35)',
              borderRadius: 20,
              padding: '6px 18px',
              fontSize: '0.82rem',
              fontWeight: 600,
              letterSpacing: 1,
              textTransform: 'uppercase',
              marginBottom: 20,
            }}
          >
            Legal
          </span>
          <h1
            style={{
              fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              letterSpacing: -1,
              marginBottom: 16,
            }}
          >
            Terms & Conditions
          </h1>
          <p style={{ fontSize: '1.05rem', opacity: 0.9, marginBottom: 8 }}>
            Effective date: 23 June 2026 · Version 1.0
          </p>
          <p style={{ fontSize: '0.9rem', opacity: 0.75 }}>
            SpotFinder Kenya Limited · Governed by the laws of Kenya
          </p>
        </div>
      </section>

      {/* Notice banner */}
      <div
        style={{
          background: '#FFF8E1',
          borderBottom: '1px solid #FFE082',
          padding: '14px 24px',
          textAlign: 'center',
          fontSize: '0.88rem',
          color: '#795548',
        }}
      >
        This document is a draft template prepared for review by qualified legal counsel in Kenya before publication.
      </div>

      {/* Body */}
      <section style={{ background: '#F5F5F5', padding: '60px 24px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>

          {/* Intro */}
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '32px 36px',
              marginBottom: 24,
              borderLeft: '4px solid #00A878',
            }}
          >
            <p style={{ color: '#444', lineHeight: 1.8, margin: 0 }}>
              These Terms and Conditions govern your use of the SpotFinder platform — including our
              mobile apps, web portals, API services, and payment pages. Please read them carefully.
              By using SpotFinder you agree to these Terms.
            </p>
          </div>

          {/* Sections */}
          {sections.map((sec) => (
            <div
              key={sec.number}
              id={'section-' + sec.number}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: '32px 36px',
                marginBottom: 20,
              }}
            >
              {/* Section header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
                <span
                  style={{
                    background: '#E8F5F1',
                    color: '#00A878',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    borderRadius: 8,
                    padding: '4px 12px',
                    whiteSpace: 'nowrap',
                    marginTop: 2,
                  }}
                >
                  {sec.number}
                </span>
                <h2
                  style={{
                    fontWeight: 800,
                    fontSize: '1.2rem',
                    color: '#0D1117',
                    margin: 0,
                  }}
                >
                  {sec.title}
                </h2>
              </div>

              {/* Content */}
              {sec.content && (
                <p style={{ color: '#444', lineHeight: 1.8, marginBottom: sec.bullets || sec.subsections || sec.definitions ? 16 : 0 }}>
                  {sec.content}
                </p>
              )}

              {/* Definitions */}
              {sec.definitions && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {sec.definitions.map((d) => (
                    <div
                      key={d.term}
                      style={{
                        display: 'flex',
                        gap: 12,
                        padding: '12px 16px',
                        background: '#F8FFFE',
                        borderRadius: 10,
                        border: '1px solid #E0F2EE',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#00A878', minWidth: 130, fontSize: '0.9rem' }}>
                        {d.term}
                      </span>
                      <span style={{ color: '#444', fontSize: '0.9rem', lineHeight: 1.7 }}>{d.def}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Bullets */}
              {sec.bullets && (
                <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sec.bullets.map((b, i) => (
                    <li
                      key={i}
                      style={{ display: 'flex', gap: 12, alignItems: 'flex-start', color: '#444', lineHeight: 1.7, fontSize: '0.95rem' }}
                    >
                      <span style={{ color: '#00A878', fontWeight: 700, marginTop: 2, flexShrink: 0 }}>→</span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}

              {/* Subsections */}
              {sec.subsections && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {sec.subsections.map((sub) => (
                    <div key={sub.title} style={{ borderLeft: '3px solid #E8F5F1', paddingLeft: 20 }}>
                      <h3 style={{ fontWeight: 700, fontSize: '1rem', color: '#1B2A4A', marginBottom: 10 }}>
                        {sub.title}
                      </h3>
                      {sub.content && (
                        <p style={{ color: '#444', lineHeight: 1.8, margin: 0 }}>{sub.content}</p>
                      )}
                      {sub.bullets && (
                        <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {sub.bullets.map((b, i) => (
                            <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', color: '#444', lineHeight: 1.7, fontSize: '0.92rem' }}>
                              <span style={{ color: '#00A878', fontWeight: 700, marginTop: 2, flexShrink: 0 }}>→</span>
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Footer note */}
              {sec.footer && (
                <p style={{ marginTop: 16, color: '#666', fontSize: '0.9rem', lineHeight: 1.7, fontStyle: 'italic', borderTop: '1px solid #f0f0f0', paddingTop: 14, marginBottom: 0 }}>
                  {sec.footer}
                </p>
              )}
            </div>
          ))}

          {/* Contact card */}
          <div
            style={{
              background: 'linear-gradient(135deg, #00A878, #007a58)',
              borderRadius: 16,
              padding: '36px',
              textAlign: 'center',
              color: '#fff',
              marginTop: 12,
            }}
          >
            <h3 style={{ fontWeight: 800, fontSize: '1.3rem', marginBottom: 10 }}>
              Questions about these Terms?
            </h3>
            <p style={{ opacity: 0.9, marginBottom: 20 }}>
              Contact our support team — we aim to respond within 2 business days.
            </p>
            <a
              href="mailto:support@spotfinder.co.ke"
              style={{
                display: 'inline-block',
                background: '#fff',
                color: '#00A878',
                fontWeight: 700,
                borderRadius: 10,
                padding: '12px 28px',
                textDecoration: 'none',
                fontSize: '0.95rem',
              }}
            >
              support@spotfinder.co.ke
            </a>
            <p style={{ marginTop: 20, opacity: 0.75, fontSize: '0.85rem', marginBottom: 0 }}>
              2026 SpotFinder Kenya Limited. All rights reserved.
            </p>
          </div>

        </div>
      </section>
    </main>
  );
}